/**
 * FuelCostTab Component
 * Handles daily fuel cost price entry
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { DollarSign, Fuel, Save, Loader2, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { getFuelGradeColor } from '../../../utils/fuelGradeColors';

// Format pennies/pence to display with specified decimal places
const formatPennies = (value, decimals = 3) => {
  const num = parseFloat(value);
  return isNaN(num) ? '-' : num.toFixed(decimals);
};

export const FuelCostTab = ({
  storeDetails,
  selectedDate,
  fuelCostPrices,
  setFuelCostPrices,
  previousDayCostPrices,
  saveFuelCostPrices,
  savingCostPrices
}) => {
  // Copy all current costs to new costs
  const copyCurrentToNew = () => {
    const newPrices = {};
    (storeDetails?.fuel_grades || []).forEach(grade => {
      const prevCost = previousDayCostPrices[grade.id];
      if (prevCost) {
        newPrices[grade.id] = prevCost.toString();
      }
    });
    setFuelCostPrices(newPrices);
  };

  // Check if there are any previous day costs to copy
  const hasPreviousCosts = Object.keys(previousDayCostPrices).length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="bg-cyan-500/5 border-b border-cyan-500/20 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-cyan-500" />
              <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                Daily Fuel Cost Entry
              </CardTitle>
            </div>
            <span className="text-sm text-muted-foreground">{format(selectedDate, 'dd MMM yyyy')}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Enter today's fuel cost prices - Current Cost shows previous day's entry
          </p>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="space-y-3">
            {/* Compact Cost Price Table */}
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-cyan-500/10">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-cyan-700 dark:text-cyan-400">Grade</th>
                    <th className="text-center px-3 py-2 font-medium text-cyan-700 dark:text-cyan-400 w-28">Current Cost</th>
                    <th className="text-center px-3 py-2 font-medium text-cyan-700 dark:text-cyan-400 w-28">New Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(storeDetails?.fuel_grades || []).map((grade, index) => {
                    const gradeColors = getFuelGradeColor(grade.name);
                    const prevDayCost = previousDayCostPrices[grade.id];
                    return (
                      <tr key={grade.id} className={`border-t ${index % 2 === 0 ? 'bg-muted/20' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded ${gradeColors.bg}`}>
                              <Fuel className={`h-3 w-3 ${gradeColors.text}`} />
                            </div>
                            <span className="font-medium">{grade.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`font-mono ${prevDayCost ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {prevDayCost ? formatPennies(prevDayCost, 3) : '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input
                            type="number"
                            step="0.001"
                            value={fuelCostPrices[grade.id] || ''}
                            onChange={(e) => setFuelCostPrices(prev => ({
                              ...prev,
                              [grade.id]: e.target.value
                            }))}
                            className="w-24 mx-auto h-8 text-sm font-mono text-center"
                            placeholder="0.000"
                            data-testid={`cost-price-${grade.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center">
              <Button 
                onClick={copyCurrentToNew} 
                disabled={!hasPreviousCosts}
                variant="outline"
                size="sm"
                className="border-cyan-500/50 text-cyan-600 hover:bg-cyan-500/10"
                data-testid="copy-current-costs-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Current to New
              </Button>
              <Button 
                onClick={saveFuelCostPrices} 
                disabled={savingCostPrices || Object.keys(fuelCostPrices).length === 0}
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="save-cost-prices-btn"
              >
                {savingCostPrices ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Prices
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FuelCostTab;
